<template>
    <section
        :id="slice.primary.anchorName || undefined"
        class="slice slice-content-columns slice-content-columns--classic"
        aria-label="content-columns"
        data-testid="slice-content-columns-classic"
    >
        <div class="slice-content">
            <PrismicRichText v-if="isFilled.richText(slice.primary.title)" :field="slice.primary.title" class="slice-title" />
            <PrismicRichText v-if="isFilled.richText(slice.primary.description)" :field="slice.primary.description" class="slice-description" />
            <div :class="['slice-body', 'slice-content-columns--classic__items-container']">
                <div class="slice-content-columns--classic__items">
                    <div
                        v-for="(item, i) in slice.items"
                        :key="i"
                        class="slice-content-columns--classic__item"
                        :class="{
                            'slice-content-columns--classic__item--with-background': slice.primary.cardWithBackground,
                        }"
                        :data-testid="`slice-content-columns-classic-item--${i}`"
                    >
                        <MktMaltPrismicImage
                            v-if="isFilled.image(item.image)"
                            :image-field="item.image"
                            class="slice-content-columns--classic__image"
                            width="100"
                            height="100"
                            :data-testid="`slice-content-columns-classic-image--${i}`"
                        />
                        <h3
                            :class="[
                                'slice-content-columns--classic__title',
                                {'slice-content-columns--classic__title--without-background': !slice.primary.cardWithBackground},
                            ]"
                            :data-testid="`slice-content-columns-classic-title--${i}`"
                        >
                            {{ item.title }}
                        </h3>
                        <PrismicRichText :field="item.text" class="slice-content-columns--classic__text" />
                    </div>
                </div>
            </div>

            <div v-if="buttons.length" class="slice-content-columns--classic__ctas">
                <div v-for="(button, idx) in buttons" :key="idx">
                    <VJoyButton :href="$withLocalePrefix(button.url)" :variant="button.variant">
                        {{ button.text }}
                    </VJoyButton>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {ContentColumnsSlice} from '@prismicio-types';
    import {MktMaltPrismicImage} from '#components';
    import {VJoyButton} from '@maltjoy/core-vue';

    type ContentColumnsClassicInZone = Extract<ContentColumnsSlice, {variation: 'contentColumnsClassic'}>;

    const props = defineProps(getSliceComponentProps<ContentColumnsClassicInZone>());

    type CtaButton = {url: string; text: string; variant: 'primary' | 'secondary' | 'main' | undefined};

    const buttons = computed<CtaButton[]>(() =>
        (props.slice.primary.ctas ?? [])
            .filter(({cta}) => isFilled.link(cta))
            .map((action) => {
                const cta = action.cta as CtaButton;
                return {url: cta.url, text: cta.text ?? '', variant: cta.variant};
            }),
    );
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-content-columns {
        &--classic {
            &__items {
                display: flex;
                justify-content: center;
                flex-wrap: wrap;
                gap: var(--joy-core-spacing-7);
                margin-top: var(--joy-core-spacing-10);

                @include mq.screen_lg(min) {
                    flex-wrap: nowrap;
                }
            }

            &__item {
                width: 100%;
                max-width: 320px;

                &--with-background {
                    padding: var(--joy-core-spacing-5) var(--joy-core-spacing-4);
                    background-color: var(--joy-color-neutral-10);
                    border-radius: var(--joy-core-radius-4);
                }
            }

            &__image {
                width: 100px;
                height: 100px;
                object-fit: contain;
            }

            &__title {
                font-weight: var(--joy-font-weight-bold);
                color: var(--joy-color-neutral-60);
                font-size: var(--joy-font-size-primary-600);
                margin: var(--joy-core-spacing-3) 0;

                @include mq.screen_md(min) {
                    font-size: var(--joy-font-size-primary-800);
                }

                &--without-background {
                    font-size: var(--joy-font-size-primary-800);

                    @include mq.screen_lg(min) {
                        font-size: var(--joy-font-size-secondary-400);
                    }
                }
            }

            &__text {
                p {
                    color: var(--joy-color-neutral-40);
                    line-height: var(--joy-line-height-large);

                    a {
                        color: var(--joy-color-neutral-60);
                        text-decoration: underline;
                        text-underline-offset: 3px;

                        &:hover {
                            color: var(--joy-color-neutral-70);
                        }
                    }
                }
            }

            &__ctas {
                display: flex;
                flex-direction: column;
                justify-content: center;
                gap: var(--joy-core-spacing-5);
                padding-top: 70px;

                @include mq.screen_xs(min) {
                    flex-direction: row;
                }

                .joy-button {
                    width: 100%;

                    @include mq.screen_sm(min) {
                        width: auto;
                    }
                }
            }
        }
    }
</style>
